import React, { useState, useCallback } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/CONSTANT';
import { crmAuthHeaders } from '../../api/DataApi';

/** POST path — tenant DB schema comes from the JWT, not this URL. */
export const OPPORTUNITY_CONVERT_TO_CONTACT_PATH = '/api/custom/opportunity';

export function ContactDetailLink({ contactId, className, children }) {
  if (!contactId) return null;
  const href = `/view/contact/${encodeURIComponent(contactId)}`;
  return (
    <a
      href={href}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        window.location.href = href;
      }}
    >
      {children}
    </a>
  );
}

function contactPayloadToLink(contact) {
  if (!contact?.id) return null;
  const fn = contact.firstname != null ? String(contact.firstname).trim() : '';
  const ln = contact.lastname != null ? String(contact.lastname).trim() : '';
  const parts = [fn, ln].filter(Boolean).join(' ').trim();
  const name =
    parts ||
    (contact.name != null ? String(contact.name).trim() : '') ||
    (contact.email != null ? String(contact.email).trim() : '') ||
    'View contact';
  return { id: String(contact.id), name };
}

/**
 * Converts an opportunity to a contact via POST /api/custom/opportunity/:id/convert-contact.
 * Self-contained (API call, loading, inline alerts). Schema is resolved server-side from auth.
 *
 * @param {string} opportunityId - Opportunity record UUID
 * @param {boolean} [alreadyConverted] - Disables button when opportunity is already flagged
 * @param {boolean} [disabled] - Extra disable (permissions, etc.)
 * @param {(data: object) => void} [onSuccess] - Receives `data` from API `{ contact, opportunityId }`
 * @param {(body: object) => void} [onAlreadyConverted] - Optional handler for idempotent 200 response
 * @param {(message: string) => void} [onError]
 */
export function OpportunityConvertToContactButton({
  opportunityId,
  alreadyConverted = false,
  disabled = false,
  onSuccess,
  onAlreadyConverted,
  onError,
  className,
  buttonClassName,
  variant = 'outline-primary',
  size = 'sm',
  children,
}) {
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleClick = useCallback(async () => {
    if (!opportunityId || loading || alreadyConverted || disabled) return;

    setFeedback(null);
    setLoading(true);
    const url = `${API_BASE_URL}${OPPORTUNITY_CONVERT_TO_CONTACT_PATH}/${encodeURIComponent(
      opportunityId
    )}/convert-contact`;

    try {
      const res = await axios.post(url, {}, { headers: { ...crmAuthHeaders() } });
      const body = res.data || {};

      if (body.alreadyConverted) {
        const message =
          body.message ||
          'This opportunity has already been converted to a contact.';
        const apiContact = body.data?.contact;
        const contact =
          apiContact?.id != null
            ? {
              id: String(apiContact.id),
              name: apiContact.name != null ? String(apiContact.name) : 'View contact',
            }
            : null;
        setFeedback({ variant: 'danger', message, contact });
        onAlreadyConverted?.(body);
        return;
      }

      if (body.success) {
        setFeedback({
          variant: 'success',
          message: body.message || 'Contact created and opportunity updated.',
        });
        onSuccess?.(body.data);
      } else {
        const message = body.message || 'Conversion failed.';
        setFeedback({ variant: 'danger', message });
        onError?.(message);
      }
    } catch (err) {
      const data = err.response?.data;
      const message =
        (typeof data === 'object' && data?.message) ||
        (typeof data === 'string' ? data : null) ||
        err.message ||
        'Request failed.';
      const contact =
        typeof data === 'object' && data?.data?.contact
          ? contactPayloadToLink(data.data.contact)
          : null;
      setFeedback({ variant: 'danger', message, contact });
      onError?.(message);
    } finally {
      setLoading(false);
    }
  }, [
    opportunityId,
    loading,
    alreadyConverted,
    disabled,
    onSuccess,
    onAlreadyConverted,
    onError,
  ]);

  if (!opportunityId) {
    return null;
  }

  return (
    <div className={className}>
      {feedback && (
        <Alert
          variant={feedback.variant}
          dismissible
          onClose={() => setFeedback(null)}
          className="mb-2 py-2 small"
        >
          <span>{feedback.message}</span>
          {feedback.contact?.id ? (
            <>
              {' '}
              <ContactDetailLink
                contactId={feedback.contact.id}
                className="alert-link fw-semibold text-break"
              >
                {feedback.contact.name}
              </ContactDetailLink>
            </>
          ) : null}
        </Alert>
      )}
      <Button
        type="button"
        className={buttonClassName}
        variant={variant}
        size={size}
        disabled={disabled || alreadyConverted || loading}
        onClick={handleClick}
      >
        {loading ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" role="status" />
            Converting…
          </>
        ) : (
          children || 'Convert to contact'
        )}
      </Button>
    </div>
  );
}
